import { lookupTable } from "data/data";

const isNonemptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const configuredAlgorithms = (algorithms) => {
  const values = Array.isArray(algorithms)
    ? algorithms
    : typeof algorithms === "string"
    ? algorithms.split(",")
    : [];
  const normalized = values.map((value) =>
    typeof value === "string" ? value.trim() : ""
  );
  if (
    normalized.length === 0 ||
    normalized.some((value) => !value) ||
    new Set(normalized).size !== normalized.length
  ) {
    return null;
  }
  return normalized;
};

const imageGuid = (image) =>
  image?.fileData?.guid || image?.data?.guid || image?.guid;

const canonicalLaterality = (laterality) => {
  if (laterality === "left" || laterality === "OS") return "left";
  if (laterality === "right" || laterality === "OD") return "right";
  return null;
};

const nestedImageEvidence = (patient) => {
  const evidence = new Map();
  [
    ["left", patient?.left],
    ["right", patient?.right],
  ].forEach(([laterality, images]) => {
    if (!Array.isArray(images)) return;
    images.forEach((image) => {
      const guid = imageGuid(image);
      if (
        isNonemptyString(guid) &&
        isNonemptyString(image?.url) &&
        canonicalLaterality(image?.data?.laterality) === laterality
      ) {
        evidence.set(`${guid}|${laterality}`, { guid, laterality });
      }
    });
  });
  return evidence.size > 0 ? Array.from(evidence.values()) : null;
};

const durableImageEvidence = (patient) => {
  const details = patient?.retinalImagesData?.retinalImagesDetails;
  if (!details || typeof details !== "object") {
    return nestedImageEvidence(patient);
  }
  const evidence = [];
  [
    ["left", details.imageLeftEye],
    ["right", details.imageRightEye],
  ].forEach(([laterality, images]) => {
    if (!Array.isArray(images)) return;
    images.forEach((image) => {
      const guid = imageGuid(image);
      if (isNonemptyString(guid)) evidence.push({ guid, laterality });
    });
  });
  const keys = evidence.map(({ guid, laterality }) => `${guid}|${laterality}`);
  if (keys.length === 0 || new Set(keys).size !== keys.length) return null;
  return evidence;
};

const modelOf = (image) => image?.Model;

const isExactTuple = (image, { guid, model, laterality }) =>
  image?.guid === guid &&
  modelOf(image) === model &&
  canonicalLaterality(image?.laterality) === laterality;

const annotationSource = (patient) => {
  const backupImages = patient?.backUpReport?.images;
  if (Array.isArray(backupImages) && backupImages.length > 0) {
    return { kind: "backup", images: backupImages };
  }
  const nestedImages = [patient?.left, patient?.right]
    .filter(Array.isArray)
    .reduce(
      (images, eye) => [
        ...images,
        ...eye.map((visible) => visible?.data).filter(Boolean),
      ],
      []
    );
  return nestedImages.length > 0
    ? { kind: "nested", images: nestedImages }
    : null;
};

const priorityFor = (status) =>
  lookupTable.find((entry) => entry.status === status)?.priority;

const deriveEye = (images, laterality, configured) => {
  const eyeImages = images.filter(
    (image) =>
      canonicalLaterality(image.laterality) === laterality &&
      configured.includes(modelOf(image))
  );
  if (eyeImages.length === 0) return null;
  const ranked = eyeImages.map((image) => ({
    image,
    status: image.status,
    priority: priorityFor(image.status),
  }));
  if (ranked.some(({ priority }) => !Number.isFinite(priority))) return null;
  return ranked.reduce((highest, candidate) =>
    candidate.priority > highest.priority ? candidate : highest
  );
};

const durableImageUrl = (patient, laterality, guid) => {
  const nestedMatch = Array.isArray(patient?.[laterality])
    ? patient[laterality].find(
        (image) =>
          imageGuid(image) === guid &&
          canonicalLaterality(image?.data?.laterality) === laterality
      )
    : null;
  if (isNonemptyString(nestedMatch?.url)) return nestedMatch.url;
  const details = patient?.retinalImagesData?.retinalImagesDetails;
  const images =
    laterality === "left" ? details?.imageLeftEye : details?.imageRightEye;
  const match = Array.isArray(images)
    ? images.find((image) => imageGuid(image) === guid)
    : null;
  return (
    match?.imgUrl ||
    match?.url ||
    match?.imageUrl ||
    match?.fileData?.imageUrl ||
    null
  );
};

const projectVisibleEye = (
  patient,
  visibleImages,
  laterality,
  winner,
  event
) => {
  if (!Array.isArray(visibleImages)) return visibleImages;
  if (visibleImages.length === 0) return visibleImages;
  const updatedImages = visibleImages.map((visible) =>
    isExactTuple(visible?.data, event)
      ? { ...visible, data: { ...visible.data, status: event.status } }
      : visible
  );
  const current = updatedImages[0];
  const matchingTupleIndex = updatedImages.findIndex((visible) =>
    isExactTuple(visible?.data, {
      guid: winner.image.guid,
      model: winner.image.Model,
      laterality,
    })
  );
  const matchingGuidIndex = updatedImages.findIndex(
    (visible) => visible?.data?.guid === winner.image.guid
  );
  const matchingIndex =
    matchingTupleIndex >= 0 ? matchingTupleIndex : matchingGuidIndex;
  const matchingImage = matchingIndex >= 0 ? updatedImages[matchingIndex] : null;
  const source = matchingImage || current;
  const currentGuid = current?.data?.guid;
  const nextUrl =
    matchingImage?.url ||
    durableImageUrl(patient, laterality, winner.image.guid) ||
    (currentGuid === winner.image.guid ? current?.url : null);
  if (currentGuid !== winner.image.guid && !nextUrl) return null;
  const sameTuple =
    source?.data?.guid === winner.image.guid &&
    source?.data?.Model === winner.image.Model &&
    canonicalLaterality(source?.data?.laterality || laterality) === laterality;
  const nextVisible = {
    ...(matchingImage || current),
    ...(nextUrl ? { url: nextUrl } : {}),
    data: sameTuple
      ? { ...source.data, ...winner.image }
      : { ...winner.image },
  };
  return matchingImage
    ? [
        nextVisible,
        ...updatedImages.filter((image, index) => index !== matchingIndex),
      ]
    : [nextVisible, ...updatedImages.slice(1)];
};

export const projectPatientStatus = (patient, event, algorithms) => {
  const configured = configuredAlgorithms(algorithms);
  const evidence = durableImageEvidence(patient);
  const source = annotationSource(patient);
  const images = source?.images;
  if (
    !patient ||
    !event ||
    !configured ||
    !evidence ||
    !Array.isArray(images) ||
    !isNonemptyString(event.guid) ||
    !isNonemptyString(event.model) ||
    (event.laterality !== "left" && event.laterality !== "right") ||
    !isNonemptyString(event.status)
  ) {
    return { outcome: "fallback" };
  }
  if (!configured.includes(event.model)) return { outcome: "fallback" };

  const completeTuples = evidence.every(({ guid, laterality }) =>
    configured.every(
      (model) =>
        images.filter((image) =>
          isExactTuple(image, { guid, model, laterality })
        ).length === 1
    )
  );
  const evidenceMatches = evidence.filter(
    ({ guid, laterality }) =>
      guid === event.guid && laterality === event.laterality
  );
  const matches = images.filter((image) => isExactTuple(image, event));
  if (!completeTuples || evidenceMatches.length !== 1 || matches.length !== 1) {
    return { outcome: "fallback" };
  }

  const nextImages = images.map((image) =>
    isExactTuple(image, event) ? { ...image, status: event.status } : image
  );
  const activeImages = nextImages.filter((image) =>
    evidence.some(
      ({ guid, laterality }) =>
        image.guid === guid &&
        canonicalLaterality(image.laterality) === laterality
    )
  );
  const eyeResults = {
    left: deriveEye(activeImages, "left", configured),
    right: deriveEye(activeImages, "right", configured),
  };
  const evidenceEyes = new Set(evidence.map(({ laterality }) => laterality));
  if (
    Array.from(evidenceEyes).some((laterality) => !eyeResults[laterality])
  ) {
    return { outcome: "fallback" };
  }
  const hasVisibleLeft = Object.prototype.hasOwnProperty.call(patient, "left");
  const hasVisibleRight = Object.prototype.hasOwnProperty.call(patient, "right");
  const nextVisibleLeft =
    hasVisibleLeft && evidenceEyes.has("left")
      ? projectVisibleEye(
          patient,
          patient.left,
          "left",
          eyeResults.left,
          event
        )
      : patient.left;
  const nextVisibleRight =
    hasVisibleRight && evidenceEyes.has("right")
      ? projectVisibleEye(
          patient,
          patient.right,
          "right",
          eyeResults.right,
          event
        )
      : patient.right;
  if (nextVisibleLeft === null || nextVisibleRight === null) {
    return { outcome: "fallback" };
  }

  const nextPatient = {
    ...patient,
    isReportAvailable: Array.from(evidenceEyes).some(
      (laterality) => eyeResults[laterality].priority > 10
    ),
  };
  if (source.kind === "backup") {
    nextPatient.backUpReport = { ...patient.backUpReport, images: nextImages };
  }
  if (evidenceEyes.has("left")) {
    nextPatient.leftEyeStatus = eyeResults.left.status;
    if (hasVisibleLeft) {
      nextPatient.left = nextVisibleLeft;
    }
  }
  if (evidenceEyes.has("right")) {
    nextPatient.rightEyeStatus = eyeResults.right.status;
    if (hasVisibleRight) {
      nextPatient.right = nextVisibleRight;
    }
  }

  return { outcome: "applied", patient: nextPatient };
};

export default projectPatientStatus;
